import { useEffect } from "react";
import { Flex, Text } from "@chakra-ui/react";
import { useData } from "vike-react/useData";
import { Shelf } from "@/components/CollectionManager/Shelf";
import { PageLoader } from "@/components/PageLoader/PageLoader";
import { AnalyticsProvider } from "@/components/Providers/AnalyticsProvider";
import { ChakraThemeProvider } from "@/components/Providers/ChakraThemeProvider";
import { IntegrationProvider } from "@/components/Providers/IntegrationProvider";
import { FundDetailPage } from "@/components/[Pages]/FundDetailPage";
import { getHeadSetting } from "@/utils/getHeadSetting";
import { usePageContext } from "@/utils/usePageContext";
import { FeaturesProvider, getFeaturesFromInstanceConfig } from "@/services/Features";
import { InstanceConfigProvider } from "@/services/InstanceConfigContext";
import type { ShareClassDashboard } from "@/services/server/v1/getShareClassDashboard";
import { useLoading } from "@/services/useLoading";
import type { FundDetailPagePathProps } from "@/types/FundDetailPagePathProps";
import type { PageData } from "@/types/PageData";
import type { ShareClassDashboardEndpoint } from "@/types/endpoints/ShareClassDashboardEndpoint";

export default function FundDetailPageEntryPoint() {
	const context = usePageContext<{ data: ShareClassDashboard }>();
	const dataResponse = useData<PageData<ShareClassDashboardEndpoint["GET"]["response"]["body"]>>();
	const { location, channel, language, externalId } = context.query as FundDetailPagePathProps;
	const [isAnyScopeLoading] = useLoading();

	useEffect(() => {
		document.title = getHeadSetting("title", context) || "";
	}, [context]);

	if ("err" in dataResponse) {
		return (
			<Flex>
				<Text>Error in response from API:</Text>
				<code>{JSON.stringify(dataResponse.err)}</code>
			</Flex>
		);
	}

	const data = dataResponse.body;
	const features = getFeaturesFromInstanceConfig(data.instanceConfig);

	return (
		<ChakraThemeProvider overrides={data.themeOverrides}>
			<IntegrationProvider>
				<AnalyticsProvider config={data.analytics}>
					<FeaturesProvider value={features}>
						<InstanceConfigProvider value={data.instanceConfig}>
							{isAnyScopeLoading && <PageLoader />}
							{!isAnyScopeLoading && (
								<FundDetailPage
									initialData={data}
									location={location}
									channel={channel}
									language={language}
									externalId={externalId}
								/>
							)}
							<Shelf
								// initialShelfPage={parseInt(shelfPage, 10)}
								translations={data.translations}
								location={location}
								channel={channel}
								language={language}
							/>
						</InstanceConfigProvider>
					</FeaturesProvider>
				</AnalyticsProvider>
			</IntegrationProvider>
		</ChakraThemeProvider>
	);
}
